import { FC, memo } from 'react';
import cn from 'classnames';
import ModuleTitle from '@base/lib/ModuleTitle';
import { IFeatureCrowdFundingBanner } from 'mod/feature/interface';
import { getModuleCode, getModuleElementId, getWrapStyle } from 'mod/feature/utils';
import titleStyles from 'app/styles/modules/title.module.less';
import styles from './index.module.less';
import { Button } from '@base/lib/Button';
import { Banner } from 'mod/featureV2/Banner';
import Countdown from '@base/lib/Countdown';
import { useTrans } from 'base/utils/i18n';
import { IActivityStatus } from '@base/crowdFunding/interface';
import { RouteEnum } from 'pro/config/routers';
import { CountdownLanguageCode, checkCountdownTimePeriod } from '@pro/crowdFunding/utils';
import { generatePath } from 'ava-util/dist/request';
import homeStyles from '@app/shop/index.module.less'
import { useShareLink } from 'base/utils/useShareLink'
import { useStore } from 'base/store';
import { useRouter } from 'next/router';
import TiledShareList from '@app/common/TiledShareList'

declare interface IProps {
    data: IFeatureCrowdFundingBanner;
}

const CrowdFundingBanner: FC<IProps> = ({ data }) => {
    const {
        endTimeStamp,
        lists,
        config: { width = 1097, height = 617 },
    } = data;

    const { t } = useTrans();

    // 分享数据
    const {
        rootStore: { state: { host } },
    } = useStore();
    const router = useRouter();


    const ActivityStatusTag: Record<IActivityStatus, string> = {
        notStarted: t('web_shop_crowdfunding_status_upcoming'),
        inProgress: t('web_shop_crowdfunding_status_live'),
        ended: t('web_shop_crowdfunding_status_successful'),
    }

    const moduleCode = getModuleCode(data);
    const moduleElementId = getModuleElementId(data);

    const item = lists[0]
    const currentTime = Math.floor(Date.now() / 1000);

    const {
        crowdFundingConfig: {
            title: crowdfundingTitle,
            activityCode,
            desc,
            startTimestamp,
            endTimestamp,
            progressPercent,
            participantAmountExchangeDisplay,
            targetAmountExchangeDisplay,
            participantCount,
        }
    } = item

    const shareUrl = generatePath(RouteEnum.CrowdFundingDetail, { code: activityCode });
    // t()中的链接替换转码问题、外部替换
    const shareTitle = t('web_shop_crowdfunding_share_desc')?.replace('{{url}}', shareUrl)

    const activityStatus: IActivityStatus = currentTime < startTimestamp
        ? 'notStarted' : currentTime < endTimestamp
            ? 'inProgress' : 'ended'
    
    return (!item) ? null : (
        <div
            id={moduleElementId}
            data-t-mod={moduleCode}
            className={homeStyles.wrapper}
            style={getWrapStyle(data)}
        >
            <div className={cn(styles.wrapper, 'g-swiper-products')}>
                <ModuleTitle
                    className={cn(titleStyles.title, homeStyles.title)}
                    moduleCode={moduleCode}
                    text={data.title}
                    link={data.readMoreLink}
                    linkText={data.readMoreTitle}
                    endTimeStamp={endTimeStamp}
                    placement="center"
                />

                <div className={cn(styles['crowd-funding-warp'], 'flex')}>
                    <div className={cn(styles['image-warp'], 'flex-1')}>
                        <Banner
                            banner={item}
                            width={1097}
                            height={1097 * height / width}
                        />
                    </div>

                    <div className={styles['info-warp']}>
                        <div className={cn(styles['info-status'],'flex flex-justify')}>
                            <span className={styles['status']}>{ActivityStatusTag[activityStatus]}</span>

                            <div className={styles['countdown']}>
                                {activityStatus === 'ended' ? (
                                    <span>{t('web_time_ended')}</span>
                                ) : (
                                    <Countdown
                                        isShowDay={true}
                                        endTime={+(activityStatus === 'notStarted' ? startTimestamp : endTimestamp || 0)}
                                        render={(o) => {
                                            const currentTimeConfig = CountdownLanguageCode[activityStatus][checkCountdownTimePeriod(o)]
                                            return `
                                            <div class="g-count">
                                                ${t(currentTimeConfig.langCode, {
                                                timeRemaining: `${+(o[currentTimeConfig.timeKey] || '')}`
                                            })}
                                            </div>
                                        `
                                        }}
                                        endRender={() => {
                                            return `
                                            <div class="g-count">
                                                 ${t(activityStatus === 'notStarted'
                                                ? 'web_shop_crowdfunding_mins_togo'
                                                : 'web_shop_crowdfunding_mins_left', {
                                                timeRemaining: 0
                                            })}
                                            </div>
                                        `
                                        }}
                                    />
                                )}
                            </div>
                        </div>

                        <p className={styles['title']}>
                            {crowdfundingTitle}
                        </p>

                        <p className={styles['desc']}>
                            {desc}
                        </p>

                        {['inProgress', 'ended'].includes(activityStatus) && (
                            <>
                                <div className={cn(styles['progress-bar'])}>
                                    <div
                                        className={cn(styles['bar'])}
                                        style={{
                                            width: `${(progressPercent || 0) * 100}%`,
                                        }}
                                    />
                                </div>

                                <div
                                    className={cn(
                                        styles['goal-warp'],
                                        'flex flex-justify'
                                    )}
                                >
                                    <div>
                                        <p className={styles['primary']}>{participantAmountExchangeDisplay}</p>
                                        <p className={styles['secondary']}>
                                            {t('web_shop_crowdfunding_pledged_of_amount', {
                                                targetAmount: targetAmountExchangeDisplay
                                            })}
                                        </p>
                                    </div>
                                    <div className={'text-right'}>
                                        <p className={styles['primary']}>{participantCount || 0}</p>
                                        <p className={styles['secondary']}>{t('web_shop_crowdfunding_backers')}</p>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className={styles['btn-wrap']}>
                            <Button
                                href={generatePath(RouteEnum.CrowdFundingDetail, { code: activityCode })}
                                target={item.target || '_blank'}
                                block={true}
                                type="primary"
                            >{t('web_shop_crowdfunding_view_details')}</Button>
                        </div>


                        <TiledShareList url={shareUrl} title={shareTitle}/>

                        {/*<div className={cn(styles['share-warp'], 'flex')}>
                            <p className={styles['share-title']}>{t('web_shop_crowdfunding_share_to')}</p>
                            <ShareChannelList
                                className={styles['share-list']}
                                shareUrls={share?.shareUrls}
                                showName={false}
                            />
                        </div>*/}
                    </div>
                </div>

            </div>
        </div>
    );
}

export default memo(CrowdFundingBanner);